import { Localized } from "@fluent/react";
import { Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useState } from "react";
import { AppId } from "../../../app/appTypes";
import { useAppDispatch } from "../../../app/hooks";
import { SaveButton } from "../../../app/components/SaveButton";
import { CloseButton } from "../../../app/components/CloseButton";
import { updateDocumentField } from "../documentsApi";
import { StructureTreeSelect } from "../../structure/StructureTreeSelect";

export interface EditDocumentStructureDialogProps {
    documentId: AppId;
    defStructureId?: AppId;
    onSuccess: (newValue: AppId) => void;
    onCancel: () => void;
}

export const EditDocumentStructureDialog = (props: EditDocumentStructureDialogProps) => {
    const dispatch = useAppDispatch();
    const [saving, setSaving] = useState(false);
    const [structureId, setStructureId] = useState<AppId>(props.defStructureId ?? "");
    const [serverError, setServerError] = useState("");

    const handleOkClick = async () => {
        setSaving(true);
        setServerError("");

        try {
            const result = await dispatch(
                updateDocumentField({
                    documentId: props.documentId,
                    fieldName: "structureId",
                    value: structureId,
                })
            );

            if (updateDocumentField.fulfilled.match(result)) {
                props.onSuccess(structureId);
            } else {
                let errorMsg = "error";

                if (result.payload && result.payload.kind === "http") {
                    const problem = result.payload.problem;

                    if (problem) {
                        errorMsg = problem.title;
                    }
                }

                setServerError(errorMsg);
            }
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : "Unexpected error";
            setServerError(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={true} fullWidth maxWidth="md">
            <DialogTitle
                sx={{
                    bgcolor: (theme) => theme.palette.grey[200],
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    py: 0.75,
                }}
            >
                <Localized id="edit">Edycja</Localized>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <StructureTreeSelect
                        defaultStructureId={structureId || undefined}
                        onStructureSelected={(value) => setStructureId(value)}
                    />
                </Box>
                {serverError && (
                    <Alert
                        sx={{ mt: 1 }}
                        onClose={() => {
                            setServerError("");
                        }}
                        severity="error"
                    >
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} onClick={handleOkClick} />
                <CloseButton loading={saving} onClick={props.onCancel} />
            </DialogActions>
        </Dialog>
    );
};